import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@/store/slices/authSlice';
import {
  Box,
  VStack,
  HStack,
  Avatar,
  Text,
  Button,
  IconButton,
  Collapse,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import { MessageCircle, Send, Trash2, MoreHorizontal, Flag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  useGetPostComments,
  useCreateComment,
  useDeleteComment,
  useGetActivityComments,
  useCreateActivityComment,
} from '@/api';
import type { Comment } from '@/api';
import { MentionInput } from '@/components/inputs';
import { RenderContentWithMentions, ReportModal } from '@/components/social';

interface CommentsProps {
  postId: number;
  isOpen: boolean;
  onToggle: () => void;
  initialComments?: Comment[]; // Optional: if provided, won't fetch from API
  isActivity?: boolean; // If true, uses activity comment endpoints instead of post endpoints
}

interface CommentItemProps {
  comment: Comment;
  postId: number;
  depth?: number;
  isActivity?: boolean;
}

function CommentItem({ comment, postId, depth = 0, isActivity = false }: CommentItemProps) {
  const currentUser = useSelector(selectCurrentUser);
  const { mutateAsync: deleteComment } = useDeleteComment();
  const { mutateAsync: createPostComment } = useCreateComment();
  const { mutateAsync: createActivityComment } = useCreateActivityComment();
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const { isOpen: isReportModalOpen, onOpen: onReportModalOpen, onClose: onReportModalClose } = useDisclosure();
  const toast = useToast();

  const handleDelete = async () => {
    try {
      await deleteComment(comment.id);
      toast({
        title: 'Comment deleted',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Failed to delete comment',
        status: 'error',
        duration: 2000,
      });
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;

    try {
      if (isActivity) {
        await createActivityComment({
          activityId: postId,
          data: { content: replyContent, parent: comment.id },
        });
      } else {
        await createPostComment({
          postId,
          data: { content: replyContent, parent: comment.id },
        });
      }
      setReplyContent('');
      setIsReplying(false);
      toast({
        title: 'Reply posted',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Failed to post reply',
        status: 'error',
        duration: 2000,
      });
    }
  };

  const maxDepth = 3;
  const marginLeft = Math.min(depth, maxDepth) * 8;

  return (
    <Box
      ml={`${marginLeft}px`}
      id={`comment-${comment.id}`}
      transition="background-color 2s ease-out"
    >
      <HStack align="start" spacing={3}>
        <Avatar
          size="sm"
          name={comment.user.name}
          src={comment.user.photoUrl || comment.user.profilePicture || undefined}
          // D66: same root cause as FeedPost.tsx's post-author Avatar and
          // MemberAvatarStack.tsx — Chakra's default fallback (no bg/color
          // set) hashes a random per-name background/text pair
          // (@chakra-ui/theme avatar.mjs) with no real WCAG guarantee.
          // Pinning the same deterministic, already-passing card-token pair
          // removes the randomness; no effect once a real photoUrl loads.
          bg="var(--card-surface-2)"
          color="var(--card-text)"
        />
        <VStack align="start" spacing={1} flex={1}>
          <HStack justify="space-between" w="100%">
            <HStack>
              <Text fontSize="sm" fontWeight="bold">
                {comment.user.name}
              </Text>
              <Text fontSize="xs" color="var(--card-text-2)">
                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              </Text>
            </HStack>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="More options"
                icon={<MoreHorizontal size={14} />}
                variant="ghost"
                size="xs"
              />
              <MenuList minW="150px">
                {currentUser?.id === comment.user.id && (
                  <MenuItem icon={<Trash2 size={14} />} onClick={handleDelete} color="var(--color-error)">
                    Delete
                  </MenuItem>
                )}
                <MenuItem icon={<Flag size={14} />} onClick={onReportModalOpen}>
                  Report
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>

          <RenderContentWithMentions content={comment.content} mentions={comment.mentions || []} />

          <HStack spacing={2}>
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<MessageCircle size={14} />}
              onClick={() => setIsReplying(!isReplying)}
            >
              Reply
            </Button>
          </HStack>

          <Collapse in={isReplying} animateOpacity>
            <HStack mt={2} w="100%">
              <MentionInput
                size="sm"
                placeholder="Write a reply... Use @ to mention someone"
                value={replyContent}
                onChange={setReplyContent}
                minH="60px"
              />
              <IconButton
                bg="var(--color-primary-purple)"
                color="white"
                _hover={{ bg: 'var(--color-purple-600)' }}
                icon={<Send size={16} />}
                aria-label="Send reply"
                onClick={handleReply}
              />
            </HStack>
          </Collapse>

          {/* Render replies */}
          {comment.replies && comment.replies.length > 0 && (
            <VStack align="stretch" spacing={3} mt={2} w="100%">
              {comment.replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                  depth={depth + 1}
                  isActivity={isActivity}
                />
              ))}
            </VStack>
          )}
        </VStack>
      </HStack>

      {/* Report Modal */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={onReportModalClose}
        contentType="comment"
        contentId={comment.id}
        contentDescription={`${comment.user.name}'s comment`}
      />
    </Box>
  );
}

export function Comments({ postId, isOpen, onToggle, initialComments, isActivity = false }: CommentsProps) {
  const currentUser = useSelector(selectCurrentUser);
  const { data: fetchedPostComments = [], isLoading: isLoadingPostComments } = useGetPostComments(postId, {
    enabled: isOpen && !initialComments && !isActivity, // Enable fetch only if initialComments are not provided and it's not an activity
  });
  const { data: fetchedActivityComments = [], isLoading: isLoadingActivityComments } = useGetActivityComments(postId, {
    enabled: isOpen && !initialComments && isActivity, // Enable fetch only if initialComments are not provided and it's an activity
  });

  // Use initialComments if provided, otherwise use fetched comments based on type
  const comments = initialComments ?? (isActivity ? fetchedActivityComments : fetchedPostComments);
  const isLoading = isActivity ? isLoadingActivityComments : isLoadingPostComments;

  const { mutateAsync: createPostComment } = useCreateComment();
  const { mutateAsync: createActivityComment } = useCreateActivityComment();
  const [newComment, setNewComment] = useState('');
  const highlightedCommentRef = useRef<HTMLElement | null>(null);
  const toast = useToast();

  // Handle deep linking to comments via URL hash
  useEffect(() => {
    if (!isOpen || comments.length === 0) return;

    const hash = window.location.hash;
    if (hash.startsWith('#comment-')) {
      const commentId = hash.replace('#comment-', '');
      const commentElement = document.getElementById(`comment-${commentId}`);

      if (commentElement) {
        // Scroll to comment after a short delay to ensure rendering
        setTimeout(() => {
          commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Highlight the comment
          commentElement.style.backgroundColor = 'var(--color-highlight-yellow)';
          highlightedCommentRef.current = commentElement;

          // Remove highlight after 2 seconds
          setTimeout(() => {
            if (highlightedCommentRef.current) {
              highlightedCommentRef.current.style.backgroundColor = 'transparent';
            }
          }, 2000);
        }, 500);
      }
    }
  }, [isOpen, comments]);

  const handleCreateComment = async () => {
    if (!newComment.trim()) {
      toast({
        title: 'Comment cannot be empty',
        status: 'error',
        duration: 2000,
      });
      return;
    }

    try {
      if (isActivity) {
        await createActivityComment({
          activityId: postId,
          data: { content: newComment },
        });
      } else {
        await createPostComment({
          postId,
          data: { content: newComment },
        });
      }
      setNewComment('');
      toast({
        title: 'Comment posted',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Failed to post comment',
        status: 'error',
        duration: 2000,
      });
    }
  };

  return (
    <Box>
      <Collapse in={isOpen} animateOpacity>
        <VStack align="stretch" spacing={4} mt={4} pt={4} borderTop="1px" borderColor="var(--card-divider)">
          {/* Comment input */}
          <HStack align="start">
            <Avatar
              size="sm"
              name={currentUser?.name || undefined}
              src={currentUser?.photoUrl || undefined}
              // D66: same root cause as the comment-author Avatar above —
              // see that comment.
              bg="var(--card-surface-2)"
              color="var(--card-text)"
            />
            <MentionInput
              placeholder="Write a comment... Use @ to mention someone"
              value={newComment}
              onChange={setNewComment}
              size="sm"
              minH="60px"
            />
            <IconButton
              bg="var(--color-primary-purple)"
              color="white"
              _hover={{ bg: 'var(--color-purple-600)' }}
              icon={<Send size={16} />}
              aria-label="Post comment"
              onClick={handleCreateComment}
            />
          </HStack>

          {/* Comments list */}
          {isLoading ? (
            <Text fontSize="sm" color="var(--card-text-2)">
              Loading comments...
            </Text>
          ) : comments.length > 0 ? (
            <VStack align="stretch" spacing={4}>
              {comments.map((comment) => (
                <CommentItem key={comment.id} comment={comment} postId={postId} isActivity={isActivity} />
              ))}
            </VStack>
          ) : (
            <Text fontSize="sm" color="var(--card-text-2)">
              No comments yet. Be the first to comment!
            </Text>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
}
